import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  Users, 
  BookOpen, 
  GraduationCap, 
  CreditCard,
  Settings,
  Palette,
  Mail,
  Globe,
  Image,
  ArrowRight,
  Shield
} from "lucide-react";

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { 
      title: "Site Branding", 
      description: "Logo, colors, and site name",
      icon: Palette, 
      path: "/superadmin/customize",
      color: "bg-violet-100 text-violet-700"
    },
    { 
      title: "Landing Page", 
      description: "Hero section and images",
      icon: Image, 
      path: "/superadmin/customize?tab=landing",
      color: "bg-blue-100 text-blue-700"
    },
    { 
      title: "Email Templates", 
      description: "Welcome and notification emails",
      icon: Mail, 
      path: "/superadmin/customize?tab=email",
      color: "bg-emerald-100 text-emerald-700"
    },
    { 
      title: "Footer & Social", 
      description: "Footer content and social links",
      icon: Globe, 
      path: "/superadmin/customize?tab=footer",
      color: "bg-orange-100 text-orange-700"
    }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="superadmin-dashboard">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-600 to-purple-700 rounded-xl flex items-center justify-center">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
              Superadmin Dashboard
            </h1>
            <p className="text-stone-600">Platform customization and management</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-violet-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats?.total_users || 0}</p>
                  <p className="text-xs text-stone-500">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats?.total_courses || 0}</p>
                  <p className="text-xs text-stone-500">Courses</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <GraduationCap className="h-5 w-5 text-emerald-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats?.total_certificates || 0}</p>
                  <p className="text-xs text-stone-500">Certificates</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-orange-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">£{stats?.net_revenue?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-stone-500">Net Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-xl font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Customize Your Platform
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action) => (
              <Link key={action.path} to={action.path}>
                <Card className="bg-white rounded-xl border border-stone-100 hover:shadow-lg hover:border-violet-200 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center mb-4`}>
                      <action.icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Outfit' }}>
                      {action.title}
                    </h3>
                    <p className="text-sm text-stone-500 mb-4">{action.description}</p>
                    <div className="flex items-center text-violet-700 text-sm font-medium">
                      Customize <ArrowRight className="h-4 w-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Full Customization Button */}
        <Card className="bg-gradient-to-r from-violet-600 to-purple-700 rounded-xl border-0">
          <CardContent className="p-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
                Full Platform Customization
              </h3>
              <p className="text-violet-100">
                Access all customization options in one place
              </p>
            </div>
            <Link to="/superadmin/customize">
              <Button 
                className="bg-white text-violet-700 hover:bg-violet-50 rounded-full px-8"
                data-testid="full-customize-btn"
              >
                <Settings className="h-4 w-4 mr-2" />
                Open Customization
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* User Breakdown */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>User Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-stone-50 rounded-xl">
                <p className="text-3xl font-bold text-stone-900">{stats?.total_students || 0}</p>
                <p className="text-sm text-stone-500">Students</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-xl">
                <p className="text-3xl font-bold text-stone-900">{stats?.total_admins || 0}</p>
                <p className="text-sm text-stone-500">Admins</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-xl">
                <p className="text-3xl font-bold text-stone-900">{stats?.total_enrollments || 0}</p>
                <p className="text-sm text-stone-500">Enrollments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SuperAdminDashboard;
